import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Sparkle, X, ArrowRight } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";

/**
 * LatestPremiumTicker — social-proof toast bottom-left.
 *
 * Behavior:
 *   - Polls /api/public/latest-premium-joiners (last 30 days, max 5)
 *   - After a 12s delay, slides in showing the most recent joiner
 *   - Auto-rotates every 15s through the list (max 3 rotations to avoid spam)
 *   - Dismissible — choice persisted 24h in localStorage
 *   - Skipped entirely if list is empty (no fake / aged-out notifications)
 *
 * Positioning:
 *   - bottom-6 left-6 (kept off the "Made with Emergent" badge in bottom-right)
 *   - z-30 to sit BELOW the ConciergeWidget chip (z-40) on mobile where they stack
 */
const DELAY_MS = 12_000;
const ROTATE_MS = 15_000;
const MAX_ROTATIONS = 3;
const DISMISS_KEY = "mc68_latest_premium_dismissed_at";
const DISMISS_HOURS = 24;

function timeAgo(iso, t) {
  if (!iso) return t.ticker.time_recent;
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return t.ticker.time_now;
  if (mins < 60) return t.ticker.time_min.replace("{n}", mins);
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t.ticker.time_hour.replace("{n}", hours);
  const days = Math.floor(hours / 24);
  if (days < 7) return t.ticker.time_day.replace("{n}", days);
  const weeks = Math.floor(days / 7);
  return t.ticker.time_week.replace("{n}", weeks);
}

export default function LatestPremiumTicker() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [rotations, setRotations] = useState(0);

  const dismiss = useCallback((persist = true) => {
    setVisible(false);
    if (persist) {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    }
  }, []);

  useEffect(() => {
    // Dismissed recently? skip.
    try {
      const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      const hoursSince = (Date.now() - at) / (1000 * 60 * 60);
      if (at && hoursSince < DISMISS_HOURS) return;
    } catch { /* noop */ }

    let active = true;
    api.get("/public/latest-premium-joiners", { params: { limit: 5 } })
      .then((r) => {
        if (!active) return;
        const list = r?.data?.items || [];
        if (list.length === 0) return;
        setItems(list);
        const t = setTimeout(() => setVisible(true), DELAY_MS);
        return () => clearTimeout(t);
      })
      .catch(() => { /* silent — never block UX */ });
    return () => { active = false; };
  }, []);

  // Rotation
  useEffect(() => {
    if (!visible || items.length === 0) return;
    const t = setInterval(() => {
      setIndex((i) => {
        const next = i + 1;
        if (next >= items.length) {
          setRotations((r) => {
            const newR = r + 1;
            if (newR >= MAX_ROTATIONS) {
              setVisible(false);
            }
            return newR;
          });
          return 0;
        }
        return next;
      });
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [visible, items.length]);

  if (!visible || items.length === 0) return null;
  const b = items[index];
  if (!b) return null;
  const isAi = b.tier === "premium_ai";

  return (
    <div
      data-testid="latest-premium-ticker"
      className="fixed bottom-6 left-6 z-30 max-w-[340px] animate-in slide-in-from-bottom-4 fade-in duration-500"
    >
      <div className="flex items-start gap-3 bg-card ring-1 ring-primary/30 shadow-xl rounded-sm px-4 py-3">
        <div className={`shrink-0 w-10 h-10 rounded-sm grid place-items-center ${isAi ? "bg-secondary/15 text-secondary" : "bg-primary/10 text-primary"}`}>
          <Sparkle size={20} weight="fill"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-primary mb-0.5">
            {isAi ? t.ticker.new_premium_ai : t.ticker.new_premium}
          </div>
          <div className="text-sm font-display font-medium text-foreground leading-tight truncate">
            {b.name}
          </div>
          <div className="text-[11px] text-muted-foreground leading-snug">
            {b.city} · {timeAgo(b.started_at, t)}
          </div>
          {b.slug && (
            <Link
              to={`/entreprise/${b.slug}`}
              onClick={() => dismiss(false)}
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
              data-testid="latest-premium-link"
            >
              {t.ticker.view_listing} <ArrowRight size={10} weight="bold"/>
            </Link>
          )}
        </div>
        <button
          type="button"
          onClick={() => dismiss(true)}
          aria-label={t.ticker.close}
          className="shrink-0 w-6 h-6 -mr-1 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition"
          data-testid="latest-premium-close"
        >
          <X size={12}/>
        </button>
      </div>
      {items.length > 1 && (
        <div className="flex justify-center gap-1 mt-1.5" data-testid="latest-premium-dots">
          {items.map((_, i) => (
            <span key={i} className={`h-1 rounded-full transition-all ${i === index ? "w-4 bg-primary" : "w-1 bg-primary/30"}`}/>
          ))}
        </div>
      )}
    </div>
  );
}
